import { useEffect, useState, useContext } from "react";
import PropTypes from "prop-types";
import { useForm } from "react-hook-form";
import {
  Container,
  Box,
  Text,
  Center,
  HStack,
  VStack,
} from "@chakra-ui/react";

// icons
import { AvailableIcons } from "../Icons/AvailableIcons.jsx";

// structural
import { ModuleBox } from "../Structural/ModuleBox.jsx";
import { Divider } from "../Divider.jsx";

// form components
import { FormSelectSimple } from "../Form/Select/FormSelectSimple.jsx";
import { TextInput } from "../Form/Input/TextInput.jsx";
import { ButtonSecondaryPlain } from "../Form/Button/ButtonSecondaryPlain.jsx";
import { ButtonPrimaryPlain } from "../Form/Button/ButtonPrimaryPlain.jsx";

// globals
import { AppContext } from "../AppContext.jsx";

// import { QuickBooksConnectionBanner } from "../QuickBooks/QuickBooksConnectionBanner.jsx";
import { useQboConnectionMeta } from "../QuickBooks/useQboConnectionMeta.jsx";

// New QBO helpers via qboProxy
import {
  getCustomersViaProxy,
  getClassesViaProxy,
} from "../QuickBooks/qboClient.jsx";

// graphql
import { fetchAllClients } from "../graphqlCompnents/Billing/fetchAllClients.jsx";
import { updateBillableClient } from "../graphqlCompnents/Billing/updateBillableClient.jsx";
import { addBillableClient } from "../graphqlCompnents/Billing/addBillableClient.jsx";
import { fetchAllDivisions } from "../graphqlCompnents/Billing/fetchAllDivisions.jsx";

// generic functions
import { levenshteinDistance } from "../functions/fuzzy.jsx";
import { todaysDatePlusNDaysYMD } from "../functions/dateTime.jsx";

export const SyncQBCustomer = () => {
  const {
    control,
    register,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: {},
  });

  // grab global context values
  const { store } = useContext(AppContext);

  // quickbooks environment, either sandbox or production
  const qbEnv = store?.qbEnvironment;

  // Only allow usage when we *know* the state and it’s usable
  const { 
    meta, 
    loading, 
    error, 
    isConnected, 
    // isExpiringSoon,
    // state 
  } = useQboConnectionMeta(qbEnv, { enabled: Boolean(qbEnv) });

  // console.log('State: ', state);

  // Connection state checking
  const hasEnv = !!qbEnv;
  const hasMeta = !!meta;
  // Don’t treat “loading” as a fatal state; just don’t allow QBO actions yet
  const canUseQbo = isConnected;

  // For UI hints:
  const showInitBanner = hasEnv && loading && !hasMeta && !error;

  // start states
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedClientObj, setSelectedClientObj] = useState(null);
  const [customersForMatching, setCustomersForMatching] = useState(null);
  const [clientsForMatching, setClientsForMatching] = useState(null);
  const [divisions, setDivisions] = useState(null);
  const [selectedDivision, setSelectedDivision] = useState(null);
  const [selectedDivisionId, setSelectedDivisionId] = useState(null);
  const [selectedActionButton, setSelectedActionButton] = useState(null);
  const [submitButtonText, setSubmitButtonText] = useState(null);
  const [customers, setCustomers] = useState(null);
  const [classes, setClasses] = useState(null);
  const [selectedQBCustomerObj, setSelectedQBCustomerObj] = useState(null);
  const [existingClients, setExistingClients] = useState(null);
  const [clientsNotSyncedToQB, setClientsNotSyncedToQB] = useState(null);

  // ----- temporary ObjectRenderer -----
  const ObjectRenderer = ({ obj }) => {
    if (!obj) {
      return null;
    }

    const renderValue = (value) => {
      if (typeof value === "object" && value !== null && !(value instanceof Array)) {
        return (
          <div style={{ marginLeft: "20px" }}>
            <ObjectRenderer obj={value} />
          </div>
        );
      } else if (Array.isArray(value)) {
        return value.map((item, index) => (
          <div key={index} style={{ marginLeft: "20px" }}>
            {renderValue(item)}
          </div>
        ));
      }
      return value?.toString?.() ?? "";
    };

    return (
      <div>
        {Object.entries(obj).map(([key, value]) => (
          <div key={key}>
            <span>{`${key}: `}</span>
            {value !== null && renderValue(value)}
          </div>
        ))}
      </div>
    );
  };

  ObjectRenderer.propTypes = {
    obj: PropTypes.object,
  };

  // ----- generic functions -----

  const resetAllData = () => {
    setSubmitButtonText(null);
    setSelectedActionButton(null);
    setSelectedClass(null);
    setSelectedClient(null);
    setSelectedClientObj(null);
    setSelectedCustomer(null);
    setSelectedQBCustomerObj(null);
    setSelectedDivision(null);
    setSelectedDivisionId(null);
    reset();
    loadClients();
  };

  const loadDivisions = async () => {
    const divisionsResp = await fetchAllDivisions();
    console.warn(" ----- divisions: ", divisionsResp);
    setDivisions(divisionsResp);
  };

  const loadQBCustomers = async () => {
    if (!qbEnv) return;
    // console.log("Loading QuickBooks customers via qboProxy...");
    // console.log("SyncQBCustomer qbEnv:", qbEnv);

    try {
      const qbCustomers = await getCustomersViaProxy({
        qbenv: qbEnv,
        app: "shared",
      });

      console.log(" 😀 ----- qbCustomers (via proxy): ", qbCustomers);

      setCustomers(qbCustomers);
      setCustomersForMatching(
        qbCustomers?.map(({ FullyQualifiedName }) => FullyQualifiedName)
      );
    } catch (err) {
      console.error("loadQBCustomers error:", err);
      if (err.code === "InvalidToken") {
        alert(
          "Your QuickBooks shared connection is no longer valid. Please reconnect from the QuickBooks Connection card. 006"
        );
        return;
      }
      alert("Failed to load QuickBooks customers. Please try again.");
    }
  };

  const loadQBClasses = async () => {
    if (!qbEnv) return;
    console.log("Loading QuickBooks classes via qboProxy...");

    try {
      const qbClasses = await getClassesViaProxy({
        qbenv: qbEnv,
        app: "shared",
      });

      console.warn(" ----- qbClasses (via proxy): ", qbClasses);
      setClasses(qbClasses);
    } catch (err) {
      console.error("loadQBClasses error:", err);
      if (err.code === "InvalidToken") {
        alert(
          "Your QuickBooks shared connection is no longer valid. Please reconnect from the QuickBooks Connection card. 007"
        );
        return;
      }
      alert("Failed to load QuickBooks classes. Please try again.");
    }
  };

  const loadClients = async () => {
    const spikeClients = await fetchAllClients();
    filterAndSortClients(spikeClients);
    setClientsForMatching(spikeClients?.map(({ contactCompany }) => contactCompany));
  };

  const filterAndSortClients = (clients) => {
    const alphabeticallySortedClients = clients.sort((a, b) =>
      a.contactCompany.localeCompare(b.contactCompany)
    );

    const notSyncedClients = alphabeticallySortedClients.filter(
      (client) => !client.isSyncedToQB
    );
    const syncedClients = alphabeticallySortedClients.filter(
      (client) => client.isSyncedToQB
    );

    setClientsNotSyncedToQB([...notSyncedClients, ...syncedClients]);
    setExistingClients([
      ...notSyncedClients,
      { id: "PLACEHOLDER", contactCompany: "----- CURRENTLY SYNCED -----" },
      ...syncedClients,
    ]);
  };

  const updateClient = async (clientData) => {
    const response = await updateBillableClient(clientData);
    return response;
  };

  const addClient = async (clientData) => {
    const response = await addBillableClient(clientData);
    return response;
  };

  const findClosestMatch = (arr, target, source) => {
    if (!arr?.length || !target || !source) {
      return null;
    }

    let currentClosestMatch = arr[0];
    let smallestDistance = levenshteinDistance(target, currentClosestMatch);

    arr.slice(1).forEach((name) => {
      const distance = levenshteinDistance(target, name);
      if (distance < smallestDistance) {
        currentClosestMatch = name;
        smallestDistance = distance;
      }
    });

    // For now we don't need to persist this anywhere else.
    // Just guard to avoid crashing if customers is not loaded yet.
    if (source !== "clients" && customers?.length) {
      const match = customers.find(
        (customer) => customer.FullyQualifiedName === currentClosestMatch
      );
      if (!match) return null;
      // If this ever needs to be surfaced, we can set separate state here.
      // const { Id, FullyQualifiedName } = match;
      // setSomeState({ Id, FullyQualifiedName });
    }

    return currentClosestMatch;
  };

  // ----- handlers -----

  const handleCustomerChanged = (event) => {
    setSelectedCustomer(event);
    const id = event?.value;
    const customer = customers?.find((cust) => cust.Id === id);
    setSelectedQBCustomerObj(customer);
    findClosestMatch(
      clientsForMatching,
      customer?.FullyQualifiedName,
      "clients"
    );
  };

  const handleClassChanged = (option) => {
    setSelectedClass(option);
  };

  const handleDivisionChanged = (option) => {
    setSelectedDivision(option);
    const classForDivision = divisions?.find(
      (division) => division.id === option?.value
    )?.defaultQBClass;
    const qbClass = classes?.find(
      (qbClass) => qbClass.Name === classForDivision
    );
    setSelectedClass({ label: qbClass?.FullyQualifiedName, value: qbClass?.Id });

    setSelectedDivisionId(option?.value);
  };

  const handleClientChanged = (option) => {
    if (option?.value === "" || option?.label === "") {
      setSelectedClient(null);
      return;
    }

    if (option?.value === "PLACEHOLDER") {
      return;
    }

    setSelectedClient(option);
    const selectedClientNotSyncedToQB =
      clientsNotSyncedToQB?.find((client) => client.id === option?.value) || {};
    setSelectedClientObj(selectedClientNotSyncedToQB);
    if (selectedClientNotSyncedToQB?.division?.defaultQBClass) {
      const qbClass = classes?.find(
        (qbClass) =>
          qbClass.Name === selectedClientNotSyncedToQB?.division?.defaultQBClass
      );
      handleClassChanged({
        label: qbClass?.FullyQualifiedName,
        value: qbClass?.Id,
      });
    }

    findClosestMatch(
      customersForMatching,
      selectedClientNotSyncedToQB?.contactCompany,
      "customers"
    );
  };

  const handleSyncQBClientData = async (qbCustomer, action) => {
    if (!action) {
      return;
    }
    if (action !== "create" && action !== "update") {
      return;
    }
    if (!selectedDivisionId && action === "create") {
      return;
    }

    const customerName =
      qbCustomer?.FullyQualifiedName !== ""
        ? qbCustomer?.FullyQualifiedName
        : qbCustomer?.CompanyName;

    const qbCustomerData = {
      clientBillingEmail: qbCustomer?.PrimaryEmailAddr?.Address || "",
      isSyncedToQB: true,
      qbCity: qbCustomer?.ShipAddr?.City || "",
      qbCompany: qbCustomer?.CompanyName || "",
      qbCountry: qbCustomer?.ShipAddr?.Country || "",
      qbCustomer: customerName || "",
      qbCustomerType: qbCustomer?.CustomerTypeRef?.name || "",
      qbDisplayName: qbCustomer?.DisplayName || "",
      qbEmail: qbCustomer?.PrimaryEmailAddr?.Address || "",
      qbPhone: qbCustomer?.PrimaryPhone?.FreeFormNumber || "",
      qbState: qbCustomer?.ShipAddr?.CountrySubDivisionCode || "",
      qbStreetAddress: qbCustomer?.ShipAddr?.Line1 || "",
      qbZip: qbCustomer?.ShipAddr?.PostalCode || "",
      staleAfterDate: todaysDatePlusNDaysYMD(365, "-"),
      ...(qbEnv === "sandbox" && {
        qbSandboxClassId: selectedClass.value,
        qbSandboxClassName: selectedClass.label,
        qbSandboxId: qbCustomer?.Id,
      }),
      ...(qbEnv === "production" && {
        qbClassId: selectedClass.value,
        qbClassName: selectedClass.label,
        qbId: qbCustomer?.Id,
      }),
      // ...(action === "create" && {
      //   allSort: "all",
      //   isActive: true,
      //   divisionId: selectedDivisionId,
      //   contactCompany: qbCustomer?.CompanyName,
      // }),
      ...(action === "create" && {
        allSort: "all",
        isActive: true,
        divisionId: selectedDivisionId,
        contactCompany: qbCustomer?.CompanyName || qbCustomer?.DisplayName || "",
      }),
      ...(action === "update" && {
        id: selectedClientObj.id,
      }),
    };

    if (action === "update") {
      await updateClient(qbCustomerData);
    } else if (action === "create") {
      await addClient(qbCustomerData);
    }

    resetAllData();
  };

  const handleSelectAction = (action) => {
    if (!action) return;

    if (action === "create") {
      setSubmitButtonText("Create New Client");
      setSelectedActionButton("create");
    } else if (action === "update") {
      setSubmitButtonText("Update Existing Client");
      setSelectedActionButton("update");
    }
  };

  // ----- effects -----

  // Bootstrap: load data once we know the env
  useEffect(() => {
    if (!qbEnv) return;

    const bootstrap = async () => {
      try {
        await Promise.all([
          loadQBCustomers(),
          loadQBClasses(),
          loadDivisions(),
          loadClients(),
        ]);
      } catch (err) {
        console.error("SyncQBCustomer bootstrap failed:", err);
      }
    };

    bootstrap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qbEnv]);

  let isDebug = false;

  if (!hasEnv) {
    return (
      <Container maxW="1600" mb={12}>
        <ModuleBox>
          <Text>
            QuickBooks environment is not configured. Set the QuickBooks
            environment in the Admin dashboard.
          </Text>
        </ModuleBox>
      </Container>
    );
  }

  if (!canUseQbo && !loading && !error && hasEnv) {
    return (
      <Container maxW="1600" mb={12}>
        <ModuleBox>
          <Text>
            QuickBooks shared connection is not active. An admin may need to
            re-authorize QuickBooks from the Admin dashboard.
          </Text>
        </ModuleBox>
      </Container>
    );
  }

  return (
    <Container as="form" maxW="1600" mb={12}>
      <>
        {showInitBanner && (
          <ModuleBox mb={4}>
            <Text>
              Checking QuickBooks shared connection…
            </Text>
          </ModuleBox>
        )}
        
        {/* Action selection */}
        {!selectedActionButton && (
          <ModuleBox>
            <VStack align={"left"}>
              <Box w={"100%"} paddingBottom={"25px"}>
                <Text textStyle={"heading-1"}>
                  What would you like to do?
                </Text>
              </Box>
              <HStack>
                <Box
                  w={"320px"}
                  bg={"var(--dark-menu-background)"}
                  borderRadius={"6px"}
                >
                  <VStack>
                    <Box paddingTop={"25px"} paddingBottom={"11"}>
                      <AvailableIcons
                        boxSize={"53px"}
                        color={"var(--success-green)"}
                        iconRef={"add"}
                      />
                    </Box>
                    <Box>
                      <Text textStyle="heading-2">Create New Client</Text>
                    </Box>
                    <Box w={"80%"}>
                      <Text align={"center"} textStyle="dark-heading-2">
                        Create a new client and connect it to a QuickBooks
                        customer.
                      </Text>
                    </Box>
                    <Box w={"48%"} py={"25px"}>
                      <ButtonPrimaryPlain
                        isDisabled={
                          selectedActionButton === "update" || !canUseQbo
                        }
                        type="button"
                        width="100%"
                        onClick={() => {
                          if (!canUseQbo) {
                            console.warn("Create selection blocked: QuickBooks shared connection is not usable.");
                            return;
                          }
                          handleSelectAction("create");
                        }}
                        name="select_new_client"
                        value="Select"
                      />
                    </Box>
                  </VStack>
                </Box>
                <Box w={"16px"}></Box>
                <Box
                  w={"320px"}
                  bg={"var(--dark-menu-background)"}
                  borderRadius={"6px"}
                >
                  <VStack>
                    <Box paddingTop={"25px"} paddingBottom={"11"}>
                      <AvailableIcons
                        boxSize={"53px"}
                        color={"var(--success-green)"}
                        iconRef={"refresh"}
                      />
                    </Box>
                    <Box>
                      <Text textStyle="heading-2">
                        Update Existing Client
                      </Text>
                    </Box>
                    <Box w={"80%"}>
                      <Text align={"center"} textStyle="dark-heading-2">
                        Update an existing client with an updated QuickBooks
                        customer.
                      </Text>
                    </Box>
                    <Box w={"48%"} py={"25px"}>
                      <ButtonPrimaryPlain
                        isDisabled={
                          selectedActionButton === "create" || !canUseQbo
                        }
                        type="button"
                        width="100%"
                        onClick={() => {
                          if (!canUseQbo) {
                            console.warn("Update selection blocked: QuickBooks shared connection is not usable.");
                            return;
                          }
                          handleSelectAction("update");
                        }}
                        name="select_update_client"
                        value="Select"
                      />
                    </Box>
                  </VStack>
                </Box>
              </HStack>
            </VStack>
          </ModuleBox>
        )}

        {/* CREATE FLOW */}
        {selectedActionButton === "create" && (
          <>
            <ModuleBox>
              <Box>
                <HStack>
                  <Box paddingTop={"6px"}>
                    <AvailableIcons
                      boxSize={"25px"}
                      color={"var(--success-green)"}
                      iconRef={"add"}
                    />
                  </Box>
                  <Box>
                    <Text textStyle="heading-1">Create New Client</Text>
                  </Box>
                </HStack>
              </Box>
            </ModuleBox>

            <ModuleBox>
              <Box>
                <Text textStyle="heading-2">
                  1. Select QuickBooks Customer
                </Text>
              </Box>
              <Box
                paddingTop={"5px"}
                paddingBottom={"25px"}
                px={"25px"}
                w={"50%"}
              >
                <FormSelectSimple
                  key="select_customer"
                  register={register}
                  control={control}
                  errors={errors}
                  isRequired={true}
                  fieldname="selectCustomer"
                  optionsArray={customers?.map(
                    ({ Id, FullyQualifiedName }) => ({
                      label: FullyQualifiedName,
                      value: Id,
                    })
                  )}
                  onChange={(e) => handleCustomerChanged(e)}
                  selectedoption={selectedCustomer}
                  placeholder={"Select customer or start typing"}
                  isDisabled={!customers?.length}
                />
              </Box>
              <Divider />
              <Box paddingTop={"25px"}>
                <Text textStyle="heading-2">
                  2. Select QuickBooks Class
                </Text>
              </Box>
              <Box
                paddingTop={"5px"}
                paddingBottom={"25px"}
                px={"25px"}
                w={"50%"}
              >
                <FormSelectSimple
                  key="select_class"
                  register={register}
                  control={control}
                  errors={errors}
                  isRequired={true}
                  fieldname="selectClass"
                  placeholder={"Select class or start typing"}
                  optionsArray={classes?.map(({ Name, Id }) => ({
                    label: Name,
                    value: Id,
                  }))}
                  onChange={(e) => handleClassChanged(e)}
                  selectedoption={selectedClass}
                  isDisabled={!classes?.length}
                />
              </Box>
              <Divider />
              <Box paddingTop={"25px"}>
                <Text textStyle="heading-2">3. Select Division</Text>
              </Box>
              <Box
                paddingTop={"5px"}
                paddingBottom={"25px"}
                px={"25px"}
                w={"50%"}
              >
                <FormSelectSimple
                  key="select_division"
                  register={register}
                  control={control}
                  errors={errors}
                  isRequired={true}
                  fieldname="selectDivision"
                  placeholder={"Select division or start typing"}
                  optionsArray={divisions?.map(
                    ({ defaultQBClass, id }) => ({
                      label: defaultQBClass,
                      value: id,
                    })
                  )}
                  onChange={(e) => handleDivisionChanged(e)}
                  selectedoption={selectedDivision}
                />
              </Box>
              <Divider />
              <Box paddingTop={"25px"}>
                <Text textStyle="heading-2">4. Create New Client</Text>
              </Box>
              <Box
                paddingTop={"5px"}
                paddingBottom={"25px"}
                px={"25px"}
                w={"50%"}
              >
                <TextInput
                  register={register}
                  errors={errors}
                  fieldname="newClientName"
                  prettyname="New Client"
                  placeholder="Enter new client name"
                />
              </Box>
            </ModuleBox>
          </>
        )}

        {/* UPDATE FLOW */}
        {selectedActionButton === "update" && (
          <>
            <ModuleBox>
              <Box>
                <HStack>
                  <Box paddingTop={"6px"}>
                    <AvailableIcons
                      boxSize={"25px"}
                      color={"var(--success-green)"}
                      iconRef={"refresh"}
                    />
                  </Box>
                  <Box>
                    <Text textStyle="heading-1">
                      Update Existing Client
                    </Text>
                  </Box>
                </HStack>
              </Box>
            </ModuleBox>

            <ModuleBox>
              <Box>
                <Text textStyle="heading-2">1. Select Client</Text>
              </Box>
              <Box
                paddingTop={"5px"}
                paddingBottom={"25px"}
                px={"25px"}
                w={"50%"}
              >
                <FormSelectSimple
                  key="select_client"
                  register={register}
                  control={control}
                  errors={errors}
                  isRequired={true}
                  fieldname="selectClient"
                  optionsArray={existingClients?.map(
                    ({ id, contactCompany }) => ({
                      label: `${contactCompany}`,
                      value: id,
                    })
                  )}
                  onChange={(e) => handleClientChanged(e)}
                  selectedoption={selectedClient}
                  placeholder={"Select client or start typing"}
                />
              </Box>
              <Divider />
              <Box paddingTop={"25px"}>
                <Text textStyle="heading-2">
                  2. Select QuickBooks Customers
                </Text>
              </Box>
              <Box
                paddingTop={"5px"}
                paddingBottom={"25px"}
                px={"25px"}
                w={"50%"}
              >
                <FormSelectSimple
                  key="select_customer"
                  register={register}
                  control={control}
                  errors={errors}
                  isRequired={true}
                  fieldname="selectCustomer"
                  optionsArray={customers?.map(
                    ({ Id, FullyQualifiedName }) => ({
                      label: FullyQualifiedName,
                      value: Id,
                    })
                  )}
                  onChange={(e) => handleCustomerChanged(e)}
                  selectedoption={selectedCustomer}
                  placeholder={"Select customer or start typing"}
                  isDisabled={!customers?.length}
                />
              </Box>
            </ModuleBox>
          </>
        )}

        {(selectedActionButton === "update" ||
          selectedActionButton === "create") && (
          <ModuleBox>
            <Center>
              <HStack py={"25px"}>
                <Box>
                  <ButtonSecondaryPlain
                    type="button"
                    width={"125px"}
                    name="cancel"
                    value="Cancel"
                    onClick={() => {
                      resetAllData();
                    }}
                  />
                </Box>
                <Box w={"25px"}></Box>
                <Box>
                  <ButtonPrimaryPlain
                    type="button"
                    width="355px"
                    onClick={() => {
                      handleSyncQBClientData(
                        selectedQBCustomerObj,
                        selectedActionButton
                      );
                    }}
                    name="sync_qbt"
                    value={submitButtonText}
                  />
                </Box>
              </HStack>
            </Center>
          </ModuleBox>
        )}

        {isDebug && (
          <HStack paddingTop={"25px"}>
            <Box w={"500px"} border={"1px solid green"}>
              <Text as="span" textStyle="dark-heading-2">
                selectedQBCustomerObj:{" "}
                <ObjectRenderer obj={selectedQBCustomerObj} />
              </Text>
            </Box>

            <Box w={"500px"} border={"1px solid green"}>
              <Text as="span" textStyle="dark-heading-2">
                selectedClientObj:{" "}
                <ObjectRenderer obj={selectedClientObj} />
              </Text>
            </Box>
          </HStack>
        )}
      </>
    </Container>
  );
};